import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import SVGCanvas from '../../svgcanvas/svgcanvas';
import SvgEditService, { svgEditServiceInstance } from '../../services/svg-edit-service';
import ColoursService, { coloursServiceInstance } from '../../services/colours-service';
import PanelsService, { panelsServiceInstance } from '../../services/panels-service';
import { SvgInlineAnnotations, UpdateSvgInlineAnnotations } from './svg-inline-annotations';

export interface IBoundingBox {
    left: number;
    top: number;
    right: number;
    bottom: number;

    width: number;
    height: number;
}

export type AnnotationRenderCallback = (args: {
    svgEditService: SvgEditService,
    colourService: ColoursService,
    panelService: PanelsService,
    bbox: IBoundingBox,
    element: SVGGraphicsElement,
    svgAnnotations: JSX.Element[],
    textAnnotations: (string | JSX.Element)[],
    defs: JSX.Element[],
}) => void;

export class SvgAnnotations {
    private static annotationsContainer: SVGGElement;
    public static get OffsetX() {
        const workarea = document.getElementById("workarea");
        return -workarea.offsetLeft;
    }

    public static get OffsetY() {
        const workarea = document.getElementById("workarea");
        return -workarea.offsetTop;
    }

    private static svgBBox: IBoundingBox;
    private static svgDefs: JSX.Element[] = [];
    private static svgAnnotations: JSX.Element[] = [];
    private static textAnnotations: (string | JSX.Element)[] = [];

    private static renderCallbacks: { key: string, callback: AnnotationRenderCallback }[] = [];
    private static renderObservers: React.Dispatch<void>[] = [];

    public static BindAnnotations(canvas: SVGCanvas, svgRoot: SVGElement) {
        SvgAnnotations.annotationsContainer = canvas.addSVGElementFromJson({
            element: 'g',
            attr: {
                id: 'pdSvgAnnotations'
            }
        });
        svgRoot.append(SvgAnnotations.annotationsContainer);

        const root = ReactDOM.createRoot(SvgAnnotations.annotationsContainer);
        root.render(<SvgInlineAnnotations />);
    }

    public static UpdateAnnotations(simpleUpdate: boolean = false) {
        if (SvgAnnotations.annotationsContainer) {
            if (SvgAnnotations.annotationsContainer.parentElement.lastChild !== SvgAnnotations.annotationsContainer) {
                // Moves it to the bottom in case we've loaded a new panel since last update.
                SvgAnnotations.annotationsContainer.parentElement.appendChild(SvgAnnotations.annotationsContainer);
            }

            const els = svgEditServiceInstance.selectedElements;

            if (els.length == 1) {
                const el = els[0];

                const rootElem = svgEditServiceInstance.canvas.getRootElem();
                const workArea = document.getElementById("workarea") as HTMLDivElement;
                const selectorElem = (rootElem.getElementById("selectedBox0") ?? rootElem.getElementById("selectorParentGroup")) as SVGElement;
                const selectorTransform = selectorElem.getBoundingClientRect();

                this.svgBBox = {
                    left: selectorTransform.left + workArea.scrollLeft,
                    top: selectorTransform.top + workArea.scrollTop,
                    right: selectorTransform.right + workArea.scrollLeft,
                    bottom: selectorTransform.bottom + workArea.scrollTop,

                    width: selectorTransform.width,
                    height: selectorTransform.height,
                };

                if (!simpleUpdate) {
                    // Refetch data
                    SvgAnnotations.svgDefs = [];
                    SvgAnnotations.svgAnnotations = [];
                    SvgAnnotations.textAnnotations = [];

                    for (const c of SvgAnnotations.renderCallbacks) {
                        try {
                            // TODO: No need to pass service instances any more
                            c.callback({
                                svgEditService: svgEditServiceInstance,
                                colourService: coloursServiceInstance,
                                panelService: panelsServiceInstance,
                                bbox: SvgAnnotations.svgBBox,
                                element: el,
                                svgAnnotations: SvgAnnotations.svgAnnotations,
                                textAnnotations: SvgAnnotations.textAnnotations,
                                defs: SvgAnnotations.svgDefs
                            });
                        }
                        catch (ex) {
                            console.error(ex);
                        }
                    }
                }
            } else {
                this.svgBBox = null;
                SvgAnnotations.svgDefs = [];
                SvgAnnotations.svgAnnotations = [];
                SvgAnnotations.textAnnotations = [];
            }

            // Triggers a refresh
            UpdateSvgInlineAnnotations();
        }

        for (const observer of SvgAnnotations.renderObservers) {
            observer();
        }
    }

    public static RegisterAnnotationCallback(key: string, callback: AnnotationRenderCallback) {
        for (const cb of SvgAnnotations.renderCallbacks) {
            if (cb.key === key) {
                console.error(`Attempting to register SVG Annotation callback ${key} but it has already been registered! Ignoring.`);
                return;
            }
        }

        SvgAnnotations.renderCallbacks.push({ key, callback });
        console.info(`Registered SVG Annotator: ${key}`)
    }

    public static HasRegisteredAnnotationCallback(key: string) {
        return SvgAnnotations.renderCallbacks.some(r => r.key === key) ?? false;
    }

    public static UnegisterAnnotationCallback(key: string) {
        for (let i = 0; i < SvgAnnotations.renderCallbacks.length; ++i) {
            if (SvgAnnotations.renderCallbacks[i].key === key) {
                SvgAnnotations.renderCallbacks.splice(i, 1);
            }
        }

        console.info(`Unregistered SVG Annotator: ${key}`)
    }
}

/**
 * Refreshes the component this is called in whenever SVG Annotations change.
 * @param onChange Optional. Invoked when the SVG Annotations change.
 */
export const useAnnotationsListener = (onChange?: () => void) => {
    const [, setDummy] = React.useState<{}>();

    const doOnChange = () => {
        setDummy({});
        if (onChange) {
            onChange();
        }
    }

    React.useEffect(() => {
        SvgAnnotations["renderObservers"].push(doOnChange);

        doOnChange();

        // Remove when unmounted
        return () => {
            SvgAnnotations["renderObservers"] = SvgAnnotations["renderObservers"].filter((update) => update !== doOnChange);
        };
    }, []);
}
